import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { config } from '../config';

const API_URL = config.apiUrl;

interface LoginErrorResponse {
  error: string;
  message?: string;
  attemptsRemaining?: number;
  lockoutThreshold?: number;
  retryAfterSeconds?: number;
  lockedUntil?: string;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  const navigate = useNavigate();
  const setAuth = {
    setUser: (user: unknown, tenant: unknown, token: string, refreshToken: string) => {
      useAuthStore.setState({
        user: user as { id: string; email: string; name: string; role: string },
        tenant: tenant as { id: string; name: string; slug: string; plan: string },
        token,
        refreshToken,
        isAuthenticated: true,
      });
    },
  };

  // Countdown timer for lockout
  useEffect(() => {
    if (!lockedUntil) {
      setCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.floor((lockedUntil.getTime() - Date.now()) / 1000));
      setCountdown(remaining);

      if (remaining <= 0) {
        setLockedUntil(null);
        setError('');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Don't allow login attempts while locked out
    if (lockedUntil && lockedUntil.getTime() > Date.now()) {
      return;
    }

    setError('');
    setLoading(true);
    setAttemptsRemaining(null);

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = data as LoginErrorResponse;

        // Handle lockout (429)
        if (response.status === 429 && errorData.retryAfterSeconds) {
          setLockedUntil(new Date(Date.now() + errorData.retryAfterSeconds * 1000));
          setError(errorData.message || 'Account temporarily locked');
          return;
        }

        // Handle failed attempt with attempts remaining
        if (errorData.attemptsRemaining !== undefined) {
          setAttemptsRemaining(errorData.attemptsRemaining);
        }

        throw new Error(errorData.error || errorData.message || 'Login failed');
      }

      // Success - set auth state
      setAuth.setUser(data.user, data.tenant, data.token, data.refreshToken);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const isLockedOut = Boolean(lockedUntil && lockedUntil.getTime() > Date.now());

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-16 w-16 bg-primary-500 rounded-2xl flex items-center justify-center">
            <span className="text-3xl font-bold text-white">C</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            WP Cruddly
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to manage your WordPress sites
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Lockout countdown */}
          {isLockedOut && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="font-medium">Account temporarily locked</span>
              </div>
              <p className="text-sm">Too many failed login attempts. Please try again in:</p>
              <div className="text-2xl font-mono font-bold text-center mt-2">
                {formatCountdown(countdown)}
              </div>
            </div>
          )}

          {/* Regular error */}
          {error && !isLockedOut && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Attempts remaining warning */}
          {attemptsRemaining !== null && attemptsRemaining <= 3 && !isLockedOut && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>
                {attemptsRemaining === 0
                  ? 'This was your last attempt. Your account will be locked.'
                  : `${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} remaining before lockout`
                }
              </span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLockedOut}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLockedOut}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading || isLockedOut}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : isLockedOut ? 'Locked' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
